/**
 * Dynamic Favicon Plugin
 *
 * Changes the favicon to show a status indicator when there are live calls.
 * Similar to Railway, Vercel, and other apps that show build/deploy status in the tab.
 */

import { Plugin } from "@nuxt/types";

const FAVICON_SIZE = 32;
const BADGE_SIZE = 12;
const BADGE_OFFSET = 2;

// Emptor brand colors
const COLORS = {
  live: "#10b981", // emerald green
  liveGlow: "rgba(16, 185, 129, 0.6)",
  pink: "#e52685", // emptor-pink
  pinkDark: "#d61f69", // emptor-pink-dark
};

let originalFavicon: string | null = null;
let canvas: HTMLCanvasElement | null = null;
let animationFrame: number | null = null;
let pulsePhase = 0;

function getOriginalFavicon(): string {
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  return link?.href || "/img/favicon.ico";
}

function setFavicon(href: string): void {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement("link");
    link.rel = "icon";
    document.head.appendChild(link);
  }
  link.href = href;
}

function createCanvas(): HTMLCanvasElement {
  if (!canvas) {
    canvas = document.createElement("canvas");
    canvas.width = FAVICON_SIZE;
    canvas.height = FAVICON_SIZE;
  }
  return canvas;
}

function drawFaviconWithBadge(
  img: HTMLImageElement,
  _liveCount: number,
  phase: number
): string {
  const cvs = createCanvas();
  const ctx = cvs.getContext("2d");
  if (!ctx) return "";

  // Clear canvas
  ctx.clearRect(0, 0, FAVICON_SIZE, FAVICON_SIZE);

  // Draw original favicon
  ctx.drawImage(img, 0, 0, FAVICON_SIZE, FAVICON_SIZE);

  // Badge position (bottom-right) - moved slightly more into view
  const badgeX = FAVICON_SIZE - BADGE_SIZE - BADGE_OFFSET;
  const badgeY = FAVICON_SIZE - BADGE_SIZE - BADGE_OFFSET;
  const badgeRadius = BADGE_SIZE / 2;
  const centerX = badgeX + badgeRadius;
  const centerY = badgeY + badgeRadius;

  // Two staggered rings for more visible "ringing" effect
  const ring1Progress = phase;
  const ring2Progress = (phase + 0.5) % 1;

  // Ring 1
  const ring1Radius = badgeRadius + ring1Progress * 10;
  const ring1Opacity = (1 - ring1Progress) * 0.9;
  if (ring1Opacity > 0.05) {
    ctx.beginPath();
    ctx.arc(centerX, centerY, ring1Radius, 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(16, 185, 129, ${ring1Opacity})`;
    ctx.lineWidth = 2.5;
    ctx.stroke();
  }

  // Ring 2 (offset by half cycle)
  const ring2Radius = badgeRadius + ring2Progress * 10;
  const ring2Opacity = (1 - ring2Progress) * 0.9;
  if (ring2Opacity > 0.05) {
    ctx.beginPath();
    ctx.arc(centerX, centerY, ring2Radius, 0, Math.PI * 2);
    ctx.strokeStyle = `rgba(16, 185, 129, ${ring2Opacity})`;
    ctx.lineWidth = 2.5;
    ctx.stroke();
  }

  // White border/ring
  ctx.beginPath();
  ctx.arc(centerX, centerY, badgeRadius + 2, 0, Math.PI * 2);
  ctx.fillStyle = "#ffffff";
  ctx.fill();

  // Green badge circle
  ctx.beginPath();
  ctx.arc(centerX, centerY, badgeRadius, 0, Math.PI * 2);
  ctx.fillStyle = COLORS.live;
  ctx.fill();

  return cvs.toDataURL("image/png");
}

function drawFaviconWithRecentBadge(img: HTMLImageElement): string {
  const cvs = createCanvas();
  const ctx = cvs.getContext("2d");
  if (!ctx) return "";

  // Clear canvas
  ctx.clearRect(0, 0, FAVICON_SIZE, FAVICON_SIZE);

  // Draw original favicon
  ctx.drawImage(img, 0, 0, FAVICON_SIZE, FAVICON_SIZE);

  // Badge position (bottom-right)
  const badgeX = FAVICON_SIZE - BADGE_SIZE - BADGE_OFFSET;
  const badgeY = FAVICON_SIZE - BADGE_SIZE - BADGE_OFFSET;
  const badgeRadius = BADGE_SIZE / 2;
  const centerX = badgeX + badgeRadius;
  const centerY = badgeY + badgeRadius;

  // White border/ring
  ctx.beginPath();
  ctx.arc(centerX, centerY, badgeRadius + 2, 0, Math.PI * 2);
  ctx.fillStyle = "#ffffff";
  ctx.fill();

  // Pink badge circle (static - no animation)
  ctx.beginPath();
  ctx.arc(centerX, centerY, badgeRadius, 0, Math.PI * 2);
  ctx.fillStyle = COLORS.pink;
  ctx.fill();

  return cvs.toDataURL("image/png");
}

function countLiveCalls(store: any): number {
  const recentCalls = store.state.recentLiveCalls || [];
  return recentCalls.filter((c: any) => {
    const state = (c.state || "").toLowerCase();
    return state === "in_progress" || state === "initiated";
  }).length;
}

function countRecentCalls(store: any): number {
  const recentCalls = store.state.recentLiveCalls || [];
  return recentCalls.length;
}

let faviconImg: HTMLImageElement | null = null;
let isAnimating = false;

function startAnimation(store: any): void {
  if (isAnimating) return;
  isAnimating = true;

  if (!originalFavicon) {
    originalFavicon = getOriginalFavicon();
  }

  // Load the original favicon image
  if (!faviconImg) {
    faviconImg = new Image();
    faviconImg.crossOrigin = "anonymous";
    faviconImg.src = originalFavicon;
  }

  const animate = (): void => {
    const liveCount = countLiveCalls(store);

    if (liveCount === 0) {
      stopAnimation();
      return;
    }

    if (faviconImg?.complete) {
      // Ring expansion animation - continuous 0 to 1 cycle
      pulsePhase += 0.04; // Controls ring speed - faster for visibility
      if (pulsePhase >= 1) pulsePhase = 0;

      const dataUrl = drawFaviconWithBadge(faviconImg, liveCount, pulsePhase);
      if (dataUrl) {
        setFavicon(dataUrl);
      }
    }

    animationFrame = requestAnimationFrame(animate);
  };

  // Wait for image to load before starting animation
  if (faviconImg.complete) {
    animate();
  } else {
    faviconImg.onload = animate;
  }
}

function stopAnimation(): void {
  isAnimating = false;
  pulsePhase = 0;

  if (animationFrame) {
    cancelAnimationFrame(animationFrame);
    animationFrame = null;
  }

  // Restore original favicon
  if (originalFavicon) {
    setFavicon(originalFavicon);
  }
}

function updateFavicon(store: any): void {
  const liveCount = countLiveCalls(store);
  const recentCount = countRecentCalls(store);

  if (liveCount > 0) {
    startAnimation(store);
  } else {
    stopAnimation();

    // Show static pink badge if there are recent (but not live) calls
    if (recentCount > 0 && faviconImg?.complete) {
      const dataUrl = drawFaviconWithRecentBadge(faviconImg);
      if (dataUrl) {
        setFavicon(dataUrl);
      }
    }
  }
}

const liveFaviconPlugin: Plugin = ({ store }) => {
  if (typeof window === "undefined") return;

  // Store original favicon on load
  originalFavicon = getOriginalFavicon();

  // Pre-load the favicon image
  faviconImg = new Image();
  faviconImg.crossOrigin = "anonymous";
  faviconImg.src = originalFavicon;

  // Watch for changes to recentLiveCalls
  store.watch(
    (state: any) => state.recentLiveCalls,
    () => {
      updateFavicon(store);
    },
    { deep: true }
  );

  // Initial check (wait for image to load)
  faviconImg.onload = () => {
    updateFavicon(store);
  };

  // Also check after a delay in case image was cached
  setTimeout(() => {
    updateFavicon(store);
  }, 1000);

  // Clean up on page unload
  window.addEventListener("beforeunload", () => {
    stopAnimation();
  });
};

export default liveFaviconPlugin;
